import { SvgFileInvoice } from '@cocoonspace/icons/react/font-awesome/light/file-invoice'
import { SvgArrowRight } from '@cocoonspace/icons/react/font-awesome/regular/arrow-right'
import { useListInvoices } from '@cocoonspace/sdk-js/domains/invoices/invoices'
import type { Event } from '@cocoonspace/sdk-js/types/event'
import { useAuth } from '@cocoonspace/shared/domains/auth/hooks/use-auth.hook'
import { useCocoonApi } from '@cocoonspace/shared/hooks/cocoon-api'
import { useTranslation } from 'next-i18next'
import Link from 'next/link'
import { type ComponentProps, useEffect, useMemo, useState } from 'react'
import { Button, buttonVariants } from '~/components/ui/button'
import { Card } from '~/components/ui/card'
import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
	DialogTrigger,
} from '~/components/ui/dialog'
import { Skeleton } from '~/components/ui/skeleton'
import { Table, TableBody } from '~/components/ui/table'
import { InvoiceTableRow } from '~/domains/account/components/invoices/invoice-table-row'
import { InvoicesTableHeader } from '~/domains/account/components/invoices/invoices-table-header'
import { useExperiment } from '~/hooks/use-experiment'
import { cn } from '~/lib/utils'
import { useTracking } from '~/providers/tracking-provider'
import { useUserContext } from '~/providers/user-provider'

const InvoiceDialogContent = ({ event }: { event: Event }) => {
	const { t } = useTranslation()

	const { data: invoices } = useListInvoices({
		// @ts-expect-error TODO: Update swagger
		event: event.id,
	})

	return (
		<>
			<DialogHeader>
				<DialogTitle>
					{t('myEvents:actions.viewInvoice', {
						count: invoices?.data.length,
					})}
				</DialogTitle>
			</DialogHeader>

			<Card className='flex flex-col divide-y overflow-hidden border'>
				<Table className='w-full'>
					<InvoicesTableHeader />

					<TableBody>
						{invoices?.data?.map((invoice) => (
							<InvoiceTableRow
								key={invoice.ref}
								invoice={invoice}
							/>
						))}
					</TableBody>
				</Table>
			</Card>
		</>
	)
}

export const PastActions = ({
	event,
}: { event: Pick<Event, 'id' | 'space'> }) => {
	const { t } = useTranslation()
	const { fireAuthUser } = useAuth()
	const { track } = useTracking()

	const hasWalletExperiment = useExperiment('wallet')

	const [token, setToken] = useState('')

	useEffect(() => {
		fireAuthUser?.getIdToken().then((token) => setToken(token))
	}, [fireAuthUser])

	const { accountQuery } = useUserContext()

	const cocoonApiInstance = useCocoonApi('_axiosInstance')

	const invoiceLink = useMemo(() => {
		const url = cocoonApiInstance.defaults.baseURL

		return `${url}/invoices/event/${event.id}?token=${token}`
	}, [event, token, cocoonApiInstance])

	return (
		<>
			{!hasWalletExperiment && !accountQuery.data?.business && (
				<Link
					href={invoiceLink}
					target='_blank'
					className={buttonVariants({ variant: 'link', size: 'sm' })}
					onClick={() => track.click('eventCard-viewInvoice-button')}
				>
					<SvgFileInvoice className='mr-2' />

					{t('myEvents:actions.viewInvoice', { count: 1 })}
				</Link>
			)}

			{hasWalletExperiment && !accountQuery.data?.business && (
				<Dialog>
					<DialogTrigger asChild>
						<Button
							variant='link'
							size='sm'
							onClick={(e) => {
								e.stopPropagation()
								e.nativeEvent.preventDefault()
							}}
						>
							<SvgFileInvoice className='mr-2' />

							{t('myEvents:actions.viewInvoice', { count: 1 })}
						</Button>
					</DialogTrigger>

					<DialogContent className='max-w-[70%]'>
						<InvoiceDialogContent event={event} />
					</DialogContent>
				</Dialog>
			)}

			{event.space && (
				<Link
					href={`/${event.space.zone}/${event.space.slug}`}
					className={buttonVariants({ variant: 'link', size: 'sm' })}
					onClick={() => track.click('eventCard-bookSame-button')}
				>
					{t('myEvents:actions.bookSame')}
				</Link>
			)}
		</>
	)
}

export const EventCardFooter = ({
	event,
	isPast,
	className,
}: {
	event: Event
	isPast: boolean
} & ComponentProps<'div'>) => {
	const { t } = useTranslation()

	return (
		<div
			className={cn(
				'mt-auto flex flex-row items-center gap-4',
				isPast ? 'justify-between' : 'justify-end',
				className,
			)}
		>
			{isPast ? (
				<PastActions event={event} />
			) : (
				<div className='flex items-center justify-end gap-x-2 font-bold text-primary'>
					{t('myEvents:actions.seeEvent')}

					<SvgArrowRight />
				</div>
			)}
		</div>
	)
}

EventCardFooter.skeleton = (props: ComponentProps<'div'>) => (
	<div
		className={cn(
			`flex flex-row items-center justify-center gap-4`,
			props.className,
		)}
	>
		<Skeleton className='h-[38px] w-[130px]' />

		<Skeleton className='h-[16px] w-[130px]' />
	</div>
)
